import type * as React from 'react'
import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import type { StepTarget } from 'react-joyride'

import { Button, CoachMark } from '@buffer-mono/popcorn'

import debounce from 'lodash/debounce'
import { logError } from '~publish/legacy/utils/logError'

type CoachMarkContentStyle = React.CSSProperties & {
  '--coach-mark-max-width'?: string
}

type StepPropTypes = {
  index: number
  open?: boolean
  step: {
    title?: React.ReactNode
    content?: React.ReactNode
    data?: {
      dismissableStep?: boolean
      actions?: {
        text: string
        action: string
      }[]
    }
    target?: StepTarget
    callback?: () => void
    placement?: string
  }
  isLastStep: boolean
  closeProps: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void
  }
  primaryProps: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void
  }
  onDismiss: () => void
}

const getSpotlightPosition = (step: {
  target?: StepTarget
}): { top: string; left: string } => {
  // Use the step's target selector to determine positioning
  const target = typeof step.target === 'string' ? step.target : null

  switch (target) {
    case '[data-tour-id="selected-channel-item"]':
      // Team permissions step in sidebar
      return { top: '0', left: '-46px' }
    case '[data-tour-id="nav-item-community"]':
      // Community tab in main navigation
      return { top: '-35px', left: '0' }
    default:
      // Default positioning for posting times and schedule steps
      return { top: '-38px', left: '0px' }
  }
}

const noopScrollListener = (): void => undefined

type CoachMarkSide = 'top' | 'left' | 'right' | 'bottom'

function isCoachMarkSide(value: string | undefined): value is CoachMarkSide {
  return (
    value === 'top' ||
    value === 'left' ||
    value === 'right' ||
    value === 'bottom'
  )
}

/**
 * Cross-browser compatible scroll helper that works in all browsers
 * @param el - The element to scroll into view
 * @param options - The options for the scrollIntoView method
 * @returns A promise that resolves when the element is in view
 */
const scrollIntoView = function (
  el: HTMLElement,
  options: ScrollIntoViewOptions = {},
): Promise<unknown> {
  if (!isOutsideViewport(el)) {
    return Promise.resolve()
  }

  let removeScrollListener = noopScrollListener
  const scrollComplete =
    'onscrollend' in window
      ? new Promise<void>((resolveOnScrollEnd) => {
          const handleScrollEnd = (): void => {
            resolveOnScrollEnd()
          }
          removeScrollListener = (): void =>
            window.removeEventListener('scrollend', handleScrollEnd)
          window.addEventListener('scrollend', handleScrollEnd, { once: true })
        })
      : new Promise<void>((resolveWhenIdle) => {
          const handleScroll = debounce((_event: Event) => {
            resolveWhenIdle()
          }, 150)
          removeScrollListener = (): void => {
            handleScroll.cancel()
            window.removeEventListener('scroll', handleScroll)
          }
          window.addEventListener('scroll', handleScroll)
        })

  let maxWaitId: ReturnType<typeof setTimeout> | null = null
  const maxWait = new Promise<void>((resolveAfterMaxWait) => {
    maxWaitId = setTimeout(resolveAfterMaxWait, 2000)
  })

  el.scrollIntoView(options)

  return Promise.race([scrollComplete, maxWait]).then(() => {
    removeScrollListener()
    if (maxWaitId !== null) {
      clearTimeout(maxWaitId)
    }
    return undefined
  })
}

/**
 * Resolves a Joyride step target (selector, element, ref, or callback) to an element
 * @param target - The step target to resolve
 * @returns The resolved HTML element, or null when the target cannot be resolved
 */
function resolveTarget(target: StepTarget | undefined): HTMLElement | null {
  if (!target) {
    return null
  }
  if (typeof target === 'string') {
    return document.querySelector<HTMLElement>(target)
  }
  if (typeof target === 'function') {
    return target()
  }
  if (target instanceof HTMLElement) {
    return target
  }
  return target.current
}

/**
 * Checks if an HTML element is outside the viewport
 * @param element - The HTML element to check
 * @returns True if the element is completely outside the viewport, false otherwise
 */
function isOutsideViewport(element: HTMLElement): boolean {
  // Get the element's bounding rectangle
  const rect = element.getBoundingClientRect()

  // Get viewport dimensions
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight

  // Check if the element is completely outside the viewport
  return (
    rect.right < 0 || // Left of viewport
    rect.bottom < 0 || // Above viewport
    rect.left > viewportWidth || // Right of viewport
    rect.top > viewportHeight // Below viewport
  )
}
export function StepCoachmark({
  open,
  step,
  isLastStep,
  closeProps,
  primaryProps,
  onDismiss,
}: StepPropTypes): JSX.Element {
  const handleNextStep = (event: React.MouseEvent<HTMLElement>): void => {
    if (isLastStep) {
      closeProps.onClick(event)
      step.callback?.()
    } else {
      primaryProps.onClick(event)
    }
  }

  const handleSkipStep = (event: React.MouseEvent<HTMLElement>): void => {
    // Check if this is the "Skip" action (first action with action: 'SKIP')
    if (step.data?.actions?.[0]?.action === 'SKIP') {
      // Advance to next step without closing the tour or executing custom actions
      primaryProps.onClick(event)
    } else {
      // For other secondary actions, dismiss the tour
      onDismiss()
    }
  }

  const handlePrimaryAction = (event: React.MouseEvent<HTMLElement>): void => {
    // Check if the primary button has a custom action
    if (step.data?.actions?.[1]?.action) {
      // Close the tour so the custom action executes in the callback
      closeProps.onClick(event)
    } else {
      // No custom action, just advance to next step
      handleNextStep(event)
    }
  }

  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const element = resolveTarget(step.target)
    if (element) {
      void scrollIntoView(element, {
        block: 'center',
        inline: 'center',
      })
        .then(() => {
          setVisible(true)
          return undefined
        })
        .catch(logError)
    }
  }, [step.target])

  const { top, left } = getSpotlightPosition(step)
  const contentStyle: CoachMarkContentStyle = {
    '--coach-mark-max-width': '350px',
  }

  return (
    <CoachMark open={open && visible}>
      <CoachMark.Overlay>
        <CoachMark.Spotlight
          style={{
            '--spotlight-top': top,
            '--spotlight-left': left,
          }}
        />
      </CoachMark.Overlay>

      <CoachMark.Content
        showCloseIcon={false}
        side={isCoachMarkSide(step.placement) ? step.placement : 'bottom'}
        style={contentStyle}
      >
        <CoachMark.Title style={{ whiteSpace: 'normal' }}>
          {step.title}
        </CoachMark.Title>
        <CoachMark.Description>{step.content}</CoachMark.Description>
        <CoachMark.Footer>
          {/* If there are two actions, render both buttons */}
          {step.data?.actions?.[1] ? (
            <>
              {/* First action: Skip button (secondary) - advances to next step if action is 'SKIP' */}
              <Button variant="secondary" onClick={handleSkipStep}>
                {step.data.actions[0].text}
              </Button>
              {/* Second action: Primary action button - closes tour if has custom action, otherwise advances */}
              <Button onClick={handlePrimaryAction}>
                {step.data.actions[1].text}
              </Button>
            </>
          ) : (
            /* If there's only one action, render it as the primary button */
            <>
              {!!step.data?.dismissableStep && (
                <Button variant="secondary" onClick={onDismiss}>
                  Got It
                </Button>
              )}
              <CoachMark.Dismiss>
                <Button onClick={handleNextStep}>
                  {step.data?.actions?.[0]?.text || 'Next'}
                </Button>
              </CoachMark.Dismiss>
            </>
          )}
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
